import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Trash2 } from "lucide-react";

interface CompanyLogoProps {
  logoUrl: string | undefined;
  setLogoUrl: (url: string) => void;
  companyName: string;
}

export const CompanyLogo = ({ logoUrl, setLogoUrl, companyName }: CompanyLogoProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `company-logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const newLogoUrl = data.publicUrl;
      
      // Update profile with new logo URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ company_logo_url: newLogoUrl })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      setLogoUrl(newLogoUrl);
      toast({
        title: "Company logo uploaded successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error uploading logo",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      setDeleting(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({ company_logo_url: null })
        .eq('id', user?.id);

      if (error) throw error;

      setLogoUrl("");
      toast({
        title: "Company logo removed successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error removing logo",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const getCompanyInitial = () => {
    return companyName?.charAt(0)?.toUpperCase() || "C";
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <Avatar className="h-24 w-24">
        {logoUrl && <AvatarImage src={logoUrl} alt="Company logo" />}
        <AvatarFallback className="text-2xl">{getCompanyInitial()}</AvatarFallback>
      </Avatar>
      
      <div className="flex gap-2">
        <label htmlFor="company-logo-upload">
          <Button 
            variant="outline" 
            size="sm" 
            disabled={uploading}
            className="cursor-pointer"
            asChild
          >
            <span>
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Logo'}
            </span>
          </Button>
        </label>
        <Input
          id="company-logo-upload"
          type="file"
          accept="image/*"
          onChange={handleLogoUpload}
          className="hidden"
        />
        
        {logoUrl && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDeleteLogo}
            disabled={deleting}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        )}
      </div>
    </div>
  );
};